import type {
  IVirtualizedTreeNode,
  IVirtualizedTreeNodeModel,
} from "#/ui-kit/components/ui/virtualized-tree/virtualized-tree.types";

import {
  getFlattenedTree,
  getNodeIndexInFlattenedTree,
  getNumberOfVisibleDescendants,
} from "#/ui-kit/components/ui/virtualized-tree/virtualized-tree-helper";
import { chunk } from "es-toolkit/array";
import { markRaw, toValue, type MaybeRefOrGetter } from "vue";

export async function useVirtualizedTreeController<
  T,
  NODE extends IVirtualizedTreeNode<T> & { children: NODE[] },
  NODE_MODEL extends IVirtualizedTreeNodeModel<T> & { children: NODE_MODEL[] },
>(nodes: MaybeRefOrGetter<NODE[]>) {
  /**
   * Avoid to use reactive() to make flattenedTree reactive
   * because it will cause performance issue
   * as it triggered multiple get() and set() when Vue detecting data changes
   * so it's expensive to apply reactive attribute for large array.
   * Instead, we force refreshing VirtualisedBaseScroller view when updating nodes
   * as it contains limited amount of visible nodes so it's much faster.
   */
  const flattenedTree = markRaw<NODE_MODEL[]>(await getFlattenedTree<T, NODE, NODE_MODEL>(toValue(nodes)));
  let latestRefreshId = 0;

  async function expandNodes(updatedNode: NODE_MODEL, flattenedTree: NODE_MODEL[]): Promise<void> {
    // Expand node by inserting node's visible descendants to flattened tree.
    const visibleDescendants = await getFlattenedTree<T, NODE, NODE_MODEL>(
      [...updatedNode.children],
      [...updatedNode.parentsIndexes, updatedNode.index],
    );

    const index = getNodeIndexInFlattenedTree<T, NODE_MODEL>(updatedNode, flattenedTree);

    /**
     * Hack: chunk large array into small sub arrays and run splice() method for each
     * to avoid "RangeError: Maximum call stack size exceeded exception".
     * This exception is due to the arguments limitation of JS engine.
     */
    const chunkedVisibleDescendants = chunk(visibleDescendants, 50_000);
    let i = index + 1;
    for (const nodes of chunkedVisibleDescendants) {
      flattenedTree.splice(i, 0, ...nodes);
      i = i + nodes.length;
    }
  }

  // Remove visible decendant nodes of the target node from the flattened tree.
  async function collapseNodes(node: NODE_MODEL, flattenedTree: NODE_MODEL[]): Promise<void> {
    const index = getNodeIndexInFlattenedTree(node, flattenedTree);

    // Collapse node by removing current node's descendants from flattened tree.
    const numberOfVisibleDescendants = await getNumberOfVisibleDescendants<T, NODE_MODEL>(node, flattenedTree);
    flattenedTree.splice(index + 1, numberOfVisibleDescendants);
  }

  async function toggleNode(node: NODE_MODEL) {
    if (node.children.length === 0) return;

    if (node.state.isExpanded) {
      node.state.isExpanded = false;
      await collapseNodes(node, flattenedTree);
    } else {
      node.state.isExpanded = true;
      await expandNodes(node, flattenedTree);
    }
  }

  async function refreshFlattenedTree(): Promise<void> {
    const refreshId = ++latestRefreshId;
    const refreshedFlattenedTree = await getFlattenedTree<T, NODE, NODE_MODEL>(toValue(nodes));
    if (refreshId !== latestRefreshId) return;

    flattenedTree.splice(0, flattenedTree.length, ...refreshedFlattenedTree);
  }

  return {
    flattenedTree,
    toggleNode,
    refreshFlattenedTree,
  };
}
